import { lumi } from '@/lib/lumi'
import { useEffect, useState } from 'react'

const ACCENT = '#B9A3D9'
const ACCENT_SOFT = '#d4c5e8'

interface Triagem {
  _id: string
  name: string
  email?: string
  whatsapp?: string
  situacao: string
  p3: string
  p4: string
  p5: string[]
  p6: string
  createdAt: string
}

function formatDate(iso: string): string {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso))
  } catch { return iso }
}

const BLOCK_LABEL: Record<string, string> = {
  email: 'E-mail',
  whatsapp: 'WhatsApp',
  situacao: 'O que te trouxe até aqui',
  p3: 'O que está buscando',
  p4: 'Contato anterior',
  p5: 'Áreas de atenção',
  p6: 'Disposição para investir',
}

export default function AdminAtendimento() {
  const [items, setItems] = useState<Triagem[]>([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  useEffect(() => {
    fetchItems()
  }, [])

  async function fetchItems() {
    setLoading(true)
    try {
      const { list } = await lumi.entities.atendimento_triagem.list({
        sort: { createdAt: -1 },
        limit: 200,
      })
      setItems(list as Triagem[])
    } catch (e) {
      console.error('Erro ao buscar triagens:', e)
    } finally {
      setLoading(false)
    }
  }

  async function handleDelete(id: string) {
    setDeleting(id)
    setItems(prev => prev.filter(i => i._id !== id))
    try {
      await lumi.entities.atendimento_triagem.delete(id)
    } catch (e) {
      console.error('Erro ao excluir triagem:', e)
      fetchItems()
    } finally {
      setDeleting(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: `${ACCENT} transparent transparent transparent` }} />
          <p className="text-[13px] font-body text-[var(--ink-soft)]">Carregando triagens…</p>
        </div>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <p className="text-[15px] font-display italic text-[var(--ink-soft)] mb-2">Nenhuma triagem encontrada</p>
        <p className="text-[12px] font-body text-[var(--ink-soft)] opacity-60">
          As respostas do formulário de triagem aparecerão aqui.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Count */}
      <p className="text-[11px] font-body numerals mb-4" style={{ color: `${ACCENT_SOFT}66` }}>
        {items.length} {items.length === 1 ? 'resposta' : 'respostas'} recebida{items.length !== 1 ? 's' : ''}
      </p>

      {items.map(item => {
        const isOpen = expanded === item._id
        return (
          <div
            key={item._id}
            className="rounded-2xl border border-white/8 transition-all duration-200 overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.025)' }}
          >
            {/* Header row */}
            <div className="flex items-start justify-between gap-3 p-4 sm:p-5">
              <div className="min-w-0 flex-1">
                <p className="font-display italic text-[17px] text-[var(--ink)] leading-snug">
                  {item.name}
                </p>
                <p className="text-[12px] font-body mt-0.5" style={{ color: `${ACCENT_SOFT}99` }}>
                  {item.email?.trim() || '—'}
                </p>
                <p className="text-[12px] font-body mt-0.5 numerals" style={{ color: `${ACCENT_SOFT}99` }}>
                  {item.whatsapp?.trim() || '—'}
                </p>
                <p className="text-[12px] font-body mt-0.5 numerals" style={{ color: `${ACCENT_SOFT}77` }}>
                  {formatDate(item.createdAt)}
                </p>
                {item.p3 && (
                  <p className="text-[12px] font-body mt-1.5 italic line-clamp-1" style={{ color: `${ACCENT_SOFT}99` }}>
                    {item.p3}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Toggle */}
                <button
                  onClick={() => setExpanded(isOpen ? null : item._id)}
                  className="px-3 py-1.5 rounded-lg font-body text-[11px] tracking-[0.1em] uppercase transition-all duration-200"
                  style={{
                    background: isOpen ? `${ACCENT}22` : 'rgba(255,255,255,0.06)',
                    color: isOpen ? ACCENT_SOFT : 'var(--ink-soft)',
                    border: `1px solid ${isOpen ? `${ACCENT}44` : 'rgba(255,255,255,0.1)'}`,
                  }}
                >
                  {isOpen ? 'Fechar' : 'Ver respostas'}
                </button>

                {/* Delete */}
                <button
                  onClick={() => handleDelete(item._id)}
                  disabled={deleting === item._id}
                  className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150 hover:bg-red-500/15 active:scale-90 group disabled:opacity-40"
                  title="Excluir triagem"
                  aria-label="Excluir triagem"
                >
                  <svg className="w-4 h-4 text-[var(--ink-soft)] group-hover:text-red-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Expanded answers */}
            {isOpen && (
              <div
                className="px-4 sm:px-5 pb-5 pt-1 border-t space-y-5"
                style={{ borderColor: 'rgba(255,255,255,0.06)' }}
              >
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-1.5" style={{ color: ACCENT_SOFT }}>
                    {BLOCK_LABEL.email}
                  </p>
                  <p className="font-body type-body" style={{ color: 'rgba(232,224,213,0.8)' }}>
                    {item.email?.trim() || '—'}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-1.5" style={{ color: ACCENT_SOFT }}>
                    {BLOCK_LABEL.whatsapp}
                  </p>
                  <p className="font-body type-body numerals" style={{ color: 'rgba(232,224,213,0.8)' }}>
                    {item.whatsapp?.trim() || '—'}
                  </p>
                </div>

                {/* Situação */}
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-1.5" style={{ color: ACCENT_SOFT }}>
                    {BLOCK_LABEL.situacao}
                  </p>
                  <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.8)' }}>
                    {item.situacao || '—'}
                  </p>
                </div>

                {/* P3 */}
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-1.5" style={{ color: ACCENT_SOFT }}>
                    {BLOCK_LABEL.p3}
                  </p>
                  <p className="font-body type-body" style={{ color: 'rgba(232,224,213,0.8)' }}>
                    {item.p3 || '—'}
                  </p>
                </div>

                {/* P4 */}
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-1.5" style={{ color: ACCENT_SOFT }}>
                    {BLOCK_LABEL.p4}
                  </p>
                  <p className="font-body type-body" style={{ color: 'rgba(232,224,213,0.8)' }}>
                    {item.p4 || '—'}
                  </p>
                </div>

                {/* P5 */}
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-1.5" style={{ color: ACCENT_SOFT }}>
                    {BLOCK_LABEL.p5}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(item.p5 || []).map((area, i) => (
                      <span
                        key={i}
                        className="px-2.5 py-1 rounded-full font-body text-[12px]"
                        style={{ background: `${ACCENT}18`, color: ACCENT_SOFT, border: `1px solid ${ACCENT}33` }}
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </div>

                {/* P6 */}
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase font-body mb-1.5" style={{ color: ACCENT_SOFT }}>
                    {BLOCK_LABEL.p6}
                  </p>
                  <p className="font-body type-body" style={{ color: 'rgba(232,224,213,0.8)' }}>
                    {item.p6 || '—'}
                  </p>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
